import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { jobService } from "../services/database";
import { useAuth } from "../contexts/AuthContext";

interface JobNewFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
  isModal?: boolean;
  initialFormData?: {
    formData: {
      project_number: string;
      title: string;
      location: string;
      prep_date: string;
      ship_date: string;
      start_date: string;
      end_date: string;
      return_date: string;
      status: "upcoming" | "in_progress" | "completed" | "cancelled";
      description: string;
      client: string;
    };
    locationDetails: {
      name: string;
      address?: string;
      city?: string;
      county?: string;
      postcode?: string;
      country?: string;
    };
  };
  onFormDataChange?: (data: {
    formData: {
      project_number: string;
      title: string;
      location: string;
      prep_date: string;
      ship_date: string;
      start_date: string;
      end_date: string;
      return_date: string;
      status: "upcoming" | "in_progress" | "completed" | "cancelled";
      description: string;
      client: string;
    };
    locationDetails: {
      name: string;
      address?: string;
      city?: string;
      county?: string;
      postcode?: string;
      country?: string;
    };
  }) => void;
}

export default function JobNewForm({
  onSuccess,
  onCancel,
  isModal = false,
  initialFormData,
  onFormDataChange,
}: JobNewFormProps) {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const { currentOrganization, user } = useAuth();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    project_number: initialFormData?.formData.project_number || "",
    title: initialFormData?.formData.title || "",
    location: initialFormData?.formData.location || "",
    prep_date: initialFormData?.formData.prep_date || "",
    ship_date: initialFormData?.formData.ship_date || "",
    start_date: initialFormData?.formData.start_date || "",
    end_date: initialFormData?.formData.end_date || "",
    return_date: initialFormData?.formData.return_date || "",
    status: (initialFormData?.formData.status || "upcoming") as
      | "upcoming"
      | "in_progress"
      | "completed"
      | "cancelled",
    description: initialFormData?.formData.description || "",
    client: initialFormData?.formData.client || "",
  });

  const [locationDetails, setLocationDetails] = useState(
    initialFormData?.locationDetails || { name: "" }
  );

  const createJobMutation = useMutation({
    mutationFn: async (jobData: any) => {
      if (!currentOrganization?.id) {
        throw new Error("No organization selected");
      }
      if (!user?.id) {
        throw new Error("User not authenticated");
      }
      return jobService.addJob({
        ...jobData,
        organization_id: currentOrganization.id,
        created_by: user.id,
        updated_by: user.id,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["jobs"] });
      if (onSuccess) {
        onSuccess();
      } else if (!isModal) {
        navigate(`/${orgId}/jobs`);
      }
    },
  });

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Notify parent component of changes
    if (onFormDataChange) {
      setTimeout(() => {
        onFormDataChange({
          formData: { ...formData, [name]: value },
          locationDetails,
        });
      }, 0);
    }
  };

  const handleLocationChange = (field: string, value: string) => {
    setLocationDetails((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Update the location field with the location name for display
    if (field === "name") {
      setFormData((prev) => ({
        ...prev,
        location: value,
      }));
    }

    // Notify parent component of changes
    if (onFormDataChange) {
      setTimeout(() => {
        onFormDataChange({
          formData: {
            ...formData,
            location: field === "name" ? value : formData.location,
          },
          locationDetails: { ...locationDetails, [field]: value },
        });
      }, 0);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentOrganization?.id) return;

    // Format location details as JSON string for the location field
    const locationString = JSON.stringify(locationDetails);

    // Convert empty date strings to null to avoid timestamp errors
    const jobData = {
      ...formData,
      location: locationString, // Save location details as JSON string in location field
      prep_date:
        formData.prep_date && formData.prep_date.trim() !== ""
          ? formData.prep_date
          : null,
      ship_date:
        formData.ship_date && formData.ship_date.trim() !== ""
          ? formData.ship_date
          : null,
      start_date:
        formData.start_date && formData.start_date.trim() !== ""
          ? formData.start_date
          : null,
      end_date:
        formData.end_date && formData.end_date.trim() !== ""
          ? formData.end_date
          : null,
      return_date:
        formData.return_date && formData.return_date.trim() !== ""
          ? formData.return_date
          : null,
      equipment: [],
      crew: [],
      notes: "",
    };

    createJobMutation.mutate(jobData);
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else if (!isModal) {
      navigate(`/${orgId}/jobs`);
    }
  };

  return (
    <div className="bg-white rounded-lg">
      <h2 className="text-xl font-semibold mb-4">Create New Job</h2>

      {createJobMutation.error && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md">
          {createJobMutation.error.message}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Project Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Project Number <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="project_number"
              value={formData.project_number}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
              required
            />
          </div>

          {/* Job Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Job Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
              required
            />
          </div>

          {/* Client */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Client
            </label>
            <input
              type="text"
              name="client"
              value={formData.client}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="upcoming">Upcoming</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          {/* Prep Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Prep Date
            </label>
            <input
              type="date"
              name="prep_date"
              value={formData.prep_date}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          {/* Ship Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Ship Date
            </label>
            <input
              type="date"
              name="ship_date"
              value={formData.ship_date}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          {/* Start Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              name="start_date"
              value={formData.start_date}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          {/* End Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <input
              type="date"
              name="end_date"
              value={formData.end_date}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          {/* Return Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Return Date
            </label>
            <input
              type="date"
              name="return_date"
              value={formData.return_date}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
            placeholder="Job description..."
          />
        </div>

        {/* Location */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-3">
            Location Details
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Location Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="Location Name"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
                value={locationDetails.name}
                onChange={(e) => handleLocationChange("name", e.target.value)}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Address
              </label>
              <input
                type="text"
                placeholder="Street Address"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
                value={locationDetails.address || ""}
                onChange={(e) =>
                  handleLocationChange("address", e.target.value)
                }
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                City
              </label>
              <input
                type="text"
                placeholder="City"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
                value={locationDetails.city || ""}
                onChange={(e) => handleLocationChange("city", e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                County
              </label>
              <input
                type="text"
                placeholder="County"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
                value={locationDetails.county || ""}
                onChange={(e) => handleLocationChange("county", e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Postcode
              </label>
              <input
                type="text"
                placeholder="Postcode"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
                value={locationDetails.postcode || ""}
                onChange={(e) =>
                  handleLocationChange("postcode", e.target.value)
                }
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Country
              </label>
              <input
                type="text"
                placeholder="Country"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
                value={locationDetails.country || ""}
                onChange={(e) =>
                  handleLocationChange("country", e.target.value)
                }
              />
            </div>
          </div>
        </div>

        {/* Buttons */}
        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={handleCancel}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={createJobMutation.isPending}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {createJobMutation.isPending ? "Creating..." : "Create Job"}
          </button>
        </div>
      </form>
    </div>
  );
}
